import type { ComponentPropsWithoutRef } from 'react';
import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Segment } from '@fxtr/i18n';
import type { AnyObject } from '$util/types';
import { DEFAULT_LAT_LNG } from '$util/gmaps';
import type { AppGarage } from '$page-router/api/getGaragesByLatLong';
import { Button, DesignType } from '@/components/atoms/Button';
import { getCloudinaryConfig } from '@/util/cloudinaryConfig';
import { context } from '@/contexts/GlobalContext';
import { Img } from '@/components/atoms/Img';

import styles from './index.module.scss';

let keepMap: google.maps.Map | null;
let previousSelectedGarageId = '';
const keepMarkers: AnyObject = {};
const mapRef = createRef<HTMLDivElement>();

interface LatLngProps {
  readonly lat?: number;
  readonly lng?: number;
}

export interface LocationProps {
  readonly name: string;
  readonly centerLatLng: LatLngProps;
  readonly zoom?: number;
  readonly zoomControl?: boolean;
  readonly zoomControlOptions?: string;
}

interface TranslationsProps {
  readonly expandMapText: string;
  readonly hideMapText: string;
}

export interface MapWithGarageMarkersProps extends ComponentPropsWithoutRef<'div'> {
  readonly location?: LocationProps;
  readonly currentPosition: google.maps.LatLng | google.maps.LatLngLiteral;
  readonly selectedGarageId: string;
  readonly setSelectedGarageId: (garageId: string) => void;
  readonly garages?: AppGarage[];
  readonly translations: TranslationsProps;
  readonly icons?: {
    readonly arrowUp?: string;
    readonly arrowDown?: string;
  };
}

const getIcons = ({ segment, isFixterGarage }: AppGarage, cloudinaryUrl: string) => {
  if (segment === Segment.Motrio_FR) {
    return {
      iconActive:
        'https://res.cloudinary.com/fxtr/image/upload/v1745321564/motrio/icons/motrio_pin_active_y0lpuy.svg',
      iconInactive:
        'https://res.cloudinary.com/fxtr/image/upload/v1745321567/motrio/icons/motrio_pin_inactive_untga3.svg',
      targetWidthActive: 120,
      targetWidthInactive: 30,
    };
  }

  if (isFixterGarage) {
    return {
      iconActive: `${cloudinaryUrl}/icons/fixter_garage_map_pin_d7slup.svg`,
      iconInactive: `${cloudinaryUrl}/icons/unselected_fixter_pin_kgczbm.svg`,
      targetWidthActive: 60,
      targetWidthInactive: 40,
    };
  }
  return {
    iconActive: `${cloudinaryUrl}/icons/non_fixter_garage_map_pin_yj1ctt.png`,
    iconInactive: `${cloudinaryUrl}/icons/non_fixter_garage_map_pin_disabled_m4ftro.png`,
    targetWidthActive: 60,
    targetWidthInactive: 40,
  };
};

const calculateScaledSize = (imageUrl: string, targetWidth: number): Promise<google.maps.Size> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.height / img.width;
      const scaledHeight = Math.round(targetWidth * aspectRatio);
      resolve(new window.google.maps.Size(targetWidth, scaledHeight));
    };
    img.src = imageUrl;
  });
};

export function MapWithGarageMarkers({
  location = DEFAULT_LAT_LNG.UK,
  currentPosition,
  selectedGarageId,
  setSelectedGarageId,
  garages = [],
  translations: { expandMapText = 'Expand Map', hideMapText = 'Hide Map' },
  icons,
  className,
}: MapWithGarageMarkersProps): JSX.Element {
  const CLOUDINARY_URL = getCloudinaryConfig();

  const mapInitialised = useRef(false);

  const [showMap, setShowMap] = useState(false);
  const [showLoader, setShowLoader] = useState(true);

  const { arrowUp = 'arrows/arrow-up', arrowDown = 'arrows/arrow-down' } = icons || {};

  const setGarageMarker = useCallback(
    async (garageData: AppGarage, imageUrl: string, markerWidth: number) => {
      if (!keepMap || !garageData.lat || !garageData.lng) return;

      const scaledSize = await calculateScaledSize(imageUrl, markerWidth);

      const garageMarker = new window.google.maps.Marker({
        position: {
          lat: garageData.lat,
          lng: garageData.lng,
        },
        map: keepMap,
        icon: {
          url: imageUrl,
          scaledSize,
        },
      });

      keepMarkers[garageData.garageId] = garageMarker;

      garageMarker.addListener('click', () => {
        setSelectedGarageId(garageData.garageId);
      });
    },
    [setSelectedGarageId]
  );

  const setSelectedGarageMarker = useCallback(
    async (newSelectedGarageId: string) => {
      // Update the icon of the previous selected garage to inactive
      if (previousSelectedGarageId && keepMarkers[previousSelectedGarageId]) {
        const previousGarage = garages.find((g) => g.garageId === previousSelectedGarageId);

        if (previousGarage) {
          const { iconInactive, targetWidthInactive } = getIcons(previousGarage, CLOUDINARY_URL);
          const scaledSize = await calculateScaledSize(iconInactive, targetWidthInactive);

          keepMarkers[previousSelectedGarageId].setIcon({
            url: iconInactive,
            scaledSize,
          });
        }
      }

      // Update the icon of the new selected garage to active
      if (keepMarkers[newSelectedGarageId]) {
        const selectedGarage = garages.find((g) => g.garageId === newSelectedGarageId);
        if (selectedGarage) {
          const { iconActive, targetWidthActive } = getIcons(selectedGarage, CLOUDINARY_URL);
          const scaledSize = await calculateScaledSize(iconActive, targetWidthActive);

          keepMarkers[newSelectedGarageId].setIcon({
            url: iconActive,
            scaledSize,
            anchor: new window.google.maps.Point(scaledSize.width / 2, scaledSize.height / 1.3),
          });
        }
      }

      // Update the previousSelectedGarageId reference
      previousSelectedGarageId = newSelectedGarageId;
    },
    [CLOUDINARY_URL, garages]
  );

  const setMarkers = useCallback(() => {
    garages?.forEach((garage) => {
      if (!keepMarkers[garage.garageId]) {
        // Create a new marker if it doesn't exist
        const { iconInactive, targetWidthInactive } = getIcons(garage, CLOUDINARY_URL);
        if (iconInactive) setGarageMarker(garage, iconInactive, targetWidthInactive);
      }
    });

    // Update the currently selected garage marker
    setSelectedGarageMarker(selectedGarageId);
  }, [garages, setSelectedGarageMarker, selectedGarageId, CLOUDINARY_URL, setGarageMarker]);

  const setCurrentLocation = useCallback(
    (userPosition: google.maps.LatLng | google.maps.LatLngLiteral) => {
      if (!keepMap || !userPosition.lat || !userPosition.lng) return;

      keepMap.setCenter(userPosition);

      // eslint-disable-next-line no-new
      new window.google.maps.Marker({
        position: userPosition,
        map: keepMap,
        icon: {
          url: `${CLOUDINARY_URL}/icons/Center_cetd1k.svg`,
          scaledSize: new window.google.maps.Size(30, 30),
        },
      });
    },
    [CLOUDINARY_URL]
  );

  const loadMap = useCallback(() => {
    if (!process.browser) return;
    setShowLoader(false);

    const { centerLatLng, zoom, zoomControl, zoomControlOptions } = location;
    const { lat: centerLat, lng: centerLng } = centerLatLng;

    if (!mapInitialised.current && mapRef.current instanceof HTMLDivElement && centerLat && centerLng) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: {
          lat: centerLat,
          lng: centerLng,
        },
        zoom,
        mapTypeId: 'roadmap',
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        zoomControl,
        zoomControlOptions: {
          position:
            zoomControlOptions === 'TOP_RIGHT' ? window.google.maps.ControlPosition.TOP_RIGHT : undefined,
        },
      });
      map.data.setStyle(() => ({
        fillColor: '#58b0ff',
        fillOpacity: 0.4,
        strokeWeight: 1,
      }));

      keepMap = map;
      mapInitialised.current = true;
      setMarkers();
      if (currentPosition) setCurrentLocation(currentPosition);
    }
  }, [currentPosition, location, setCurrentLocation, setMarkers]);

  const initMap = useCallback(() => {
    if (window?.google_map_script_tag) {
      // when multiple components are initialised at the same time
      const waitScriptLoad = setInterval(() => {
        if (window.google) {
          loadMap();
          clearInterval(waitScriptLoad);
        }
      }, 100);
    } else if (!window?.google) {
      const apiKey = context.get('gMapsApiKey');
      if (!apiKey) return;
      window.google_map_script_tag = true;
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://maps.google.com/maps/api/js?key=${apiKey}`;
      script.async = true;
      const x = document.getElementsByTagName('script')[0];
      x?.parentNode?.insertBefore(script, x);
      script.addEventListener('load', () => {
        loadMap();
      });
    }
  }, [loadMap]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    initMap();
  }, [initMap]);

  useEffect(() => {
    setMarkers();
  }, [garages, setMarkers]);

  useEffect(() => {
    setSelectedGarageMarker(selectedGarageId);
  }, [selectedGarageId, setSelectedGarageMarker]);

  useEffect(() => {
    if (currentPosition) setCurrentLocation(currentPosition);
  }, [currentPosition, setCurrentLocation]);

  const showMapClicked = () => {
    if (!keepMap) initMap();
    setShowMap(!showMap);
  };

  return (
    <div className={classNames(MapWithGarageMarkers.displayName, styles.component, className)}>
      {showLoader && (
        <div className={classNames(styles.loader, 'loader')}>
          <Img
            className={styles.staticMap}
            src="https://res.cloudinary.com/fxtr/image/upload/v1656437008/landing/location/static-gmap_sbnbe3.png"
            alt="static map"
            quality={75}
            width={100}
            height={100}
            provider="cloudinary"
            loading="eager"
            decoding="sync"
            fetchPriority="high"
            priority
          />
        </div>
      )}

      <div
        ref={mapRef}
        className={classNames(
          styles.map,
          'map',
          showMap && styles.map__open,
          showMap && 'map__open',
          showLoader && styles.map__hidden
        )}
      />

      <Button
        className={classNames(styles.displayMapButton, 'displayMapButton')}
        designType={DesignType.CTA_LIGHT}
        iconId={showMap ? arrowUp : arrowDown}
        onClick={() => showMapClicked()}
      >
        {showMap ? hideMapText : expandMapText}
      </Button>
    </div>
  );
}

MapWithGarageMarkers.displayName = 'MapWithGarageMarkers';
